import {
  ApiRequestError,
  type CallOutcome,
  type SubmitCallResultInput,
  type SubmitCallResultResponse,
} from './api.js';

const STORAGE_PREFIX = 'bitrix-sim-bridge:call-result:v1';
const RETENTION_MS = 24 * 60 * 60_000;
const MAX_BACKOFF_MS = 60_000;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface PendingResult {
  version: 1;
  callId: string;
  payload: SubmitCallResultInput;
  savedAt: string;
  expiresAt: string;
  attempts: number;
  nextRetryAt: string | null;
}

export interface TerminalResult {
  version: 1;
  callId: string;
  outcome: CallOutcome;
  status: number;
  code: string | null;
  savedAt: string;
  expiresAt: string;
}

export type RetryPendingResult =
  | { state: 'none' }
  | { state: 'waiting'; nextRetryAt: string }
  | { state: 'retryable'; nextRetryAt: string }
  | { state: 'expired' }
  | { state: 'terminal'; status: number; code: string | null }
  | { state: 'processed'; response: SubmitCallResultResponse };

interface SavePendingOptions {
  now?: Date;
  expiresAt?: Date;
}

interface RetryOptions {
  now?: () => Date;
}

type Submit = (
  payload: SubmitCallResultInput,
) => Promise<SubmitCallResultResponse>;

const inFlight = new WeakMap<object, Map<string, Promise<RetryPendingResult>>>();

function pendingKey(callId: string): string {
  return `${STORAGE_PREFIX}:pending:${callId}`;
}

function terminalKey(callId: string): string {
  return `${STORAGE_PREFIX}:terminal:${callId}`;
}

function validPayload(value: unknown): value is SubmitCallResultInput {
  if (!value || typeof value !== 'object') return false;
  const payload = value as Record<string, unknown>;
  return (
    ['no_answer', 'answered', 'not_interested'].includes(String(payload.outcome))
    && (payload.nextActivityAt === null || typeof payload.nextActivityAt === 'string')
    && typeof payload.comment === 'string'
    && Array.from(payload.comment).length <= 2_000
  );
}

function parsePending(raw: string | null, callId: string): PendingResult | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<PendingResult>;
    if (
      value.version !== 1
      || value.callId !== callId
      || !validPayload(value.payload)
      || typeof value.savedAt !== 'string'
      || typeof value.expiresAt !== 'string'
      || typeof value.attempts !== 'number'
      || (value.nextRetryAt !== null && typeof value.nextRetryAt !== 'string')
      || Number.isNaN(Date.parse(value.savedAt))
      || Number.isNaN(Date.parse(value.expiresAt))
    ) return null;
    return value as PendingResult;
  } catch {
    return null;
  }
}

export function loadPendingResult(
  storage: StorageLike,
  callId: string,
): PendingResult | null {
  const key = pendingKey(callId);
  const pending = parsePending(storage.getItem(key), callId);
  if (!pending && storage.getItem(key) !== null) storage.removeItem(key);
  return pending;
}

export function loadTerminalResult(
  storage: StorageLike,
  callId: string,
): TerminalResult | null {
  const key = terminalKey(callId);
  const raw = storage.getItem(key);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<TerminalResult>;
    if (
      value.version !== 1
      || value.callId !== callId
      || !['no_answer', 'answered', 'not_interested'].includes(String(value.outcome))
      || typeof value.status !== 'number'
      || (value.code !== null && typeof value.code !== 'string')
      || typeof value.savedAt !== 'string'
      || typeof value.expiresAt !== 'string'
    ) throw new Error('invalid terminal result');
    if (Date.parse(value.expiresAt) <= Date.now()) {
      storage.removeItem(key);
      return null;
    }
    return value as TerminalResult;
  } catch {
    storage.removeItem(key);
    return null;
  }
}

export function savePendingResult(
  storage: StorageLike,
  callId: string,
  payload: SubmitCallResultInput,
  options: SavePendingOptions = {},
): PendingResult {
  const now = options.now ?? new Date();
  const record: PendingResult = {
    version: 1,
    callId,
    payload: { ...payload },
    savedAt: now.toISOString(),
    expiresAt: (options.expiresAt ?? new Date(now.getTime() + RETENTION_MS)).toISOString(),
    attempts: 0,
    nextRetryAt: null,
  };
  storage.removeItem(terminalKey(callId));
  storage.setItem(pendingKey(callId), JSON.stringify(record));
  return record;
}

function terminalStatus(error: unknown): number | null {
  if (!(error instanceof ApiRequestError)) return null;
  if (error.status < 400 || error.status >= 500) return null;
  if (error.status === 408 || error.status === 429) return null;
  return error.status;
}

function retryDelay(attempts: number): number {
  return Math.min(MAX_BACKOFF_MS, 1_000 * 2 ** Math.max(0, attempts - 1));
}

async function attemptPendingResult(
  storage: StorageLike,
  callId: string,
  submit: Submit,
  options: RetryOptions,
): Promise<RetryPendingResult> {
  const now = (options.now ?? (() => new Date()))();
  const terminal = loadTerminalResult(storage, callId);
  if (terminal) {
    return { state: 'terminal', status: terminal.status, code: terminal.code };
  }
  const pending = loadPendingResult(storage, callId);
  if (!pending) return { state: 'none' };
  if (Date.parse(pending.expiresAt) <= now.getTime()) {
    storage.removeItem(pendingKey(callId));
    return { state: 'expired' };
  }
  if (pending.nextRetryAt && Date.parse(pending.nextRetryAt) > now.getTime()) {
    return { state: 'waiting', nextRetryAt: pending.nextRetryAt };
  }

  try {
    const response = await submit({ ...pending.payload });
    if (response.status === 'processed' || response.status === 'already_processed') {
      storage.removeItem(pendingKey(callId));
      return { state: 'processed', response };
    }
    const manualReview: TerminalResult = {
      version: 1,
      callId,
      outcome: pending.payload.outcome,
      status: 422,
      code: 'manual_review',
      savedAt: now.toISOString(),
      expiresAt: pending.expiresAt,
    };
    storage.removeItem(pendingKey(callId));
    storage.setItem(terminalKey(callId), JSON.stringify(manualReview));
    return { state: 'terminal', status: 422, code: 'manual_review' };
  } catch (error) {
    const status = terminalStatus(error);
    if (status !== null) {
      const terminalRecord: TerminalResult = {
        version: 1,
        callId,
        outcome: pending.payload.outcome,
        status,
        code: error instanceof ApiRequestError ? error.code : null,
        savedAt: now.toISOString(),
        expiresAt: pending.expiresAt,
      };
      storage.removeItem(pendingKey(callId));
      storage.setItem(terminalKey(callId), JSON.stringify(terminalRecord));
      return { state: 'terminal', status, code: terminalRecord.code };
    }
    const attempts = pending.attempts + 1;
    const nextRetryAt = new Date(now.getTime() + retryDelay(attempts)).toISOString();
    storage.setItem(pendingKey(callId), JSON.stringify({
      ...pending,
      attempts,
      nextRetryAt,
    } satisfies PendingResult));
    return { state: 'retryable', nextRetryAt };
  }
}

export function retryPendingResult(
  storage: StorageLike,
  callId: string,
  submit: Submit,
  options: RetryOptions = {},
): Promise<RetryPendingResult> {
  let storageRequests = inFlight.get(storage as object);
  if (!storageRequests) {
    storageRequests = new Map();
    inFlight.set(storage as object, storageRequests);
  }
  const existing = storageRequests.get(callId);
  if (existing) return existing;
  const request = attemptPendingResult(storage, callId, submit, options)
    .finally(() => storageRequests?.delete(callId));
  storageRequests.set(callId, request);
  return request;
}
